/**
 * Typed client for the `esign-api` Appwrite function.
 *
 * Every call is a function execution carrying a short-lived user JWT, so the
 * server decides what the caller may see. The browser holds no privileges of
 * its own beyond its session.
 */
import { Account, Client, Functions, ExecutionMethod } from 'appwrite';
import type { Models } from 'appwrite';
import { APPWRITE_ENDPOINT, APPWRITE_PROJECT, ESIGN_FUNCTION_ID } from './config';

export const client = new Client().setEndpoint(APPWRITE_ENDPOINT).setProject(APPWRITE_PROJECT);
export const account = new Account(client);
const functions = new Functions(client);

/* -------------------------------------------------------------------------- */
/* Wire types - these mirror exactly what the function returns.                */
/* -------------------------------------------------------------------------- */

export type EnvelopeStatus = 'draft' | 'sent' | 'completed' | 'voided';
export type SignerStatus = 'pending' | 'viewed' | 'signed';

export interface Signer {
  id: string;
  name: string;
  email: string;
  status: SignerStatus;
  signedAt: string | null;
}

export interface Envelope {
  $id: string;
  title: string;
  status: EnvelopeStatus;
  signedFileId?: string | null;
  completedAt?: string | null;
}

/** An envelope as returned by `GET /my`, with its signer roster attached. */
export interface EnvelopeSummary extends Envelope {
  signers: Signer[];
}

export interface MyEnvelopes {
  owner: boolean;
  envelopes: EnvelopeSummary[];
}

export interface EnvelopeDetail {
  envelope: Envelope;
  signers: Signer[];
  me: { email: string; isOwner: boolean };
}

export interface DocumentFile {
  filename: string;
  contentType: string;
  base64: string;
}

export interface SignResult {
  ok: true;
  /** True when this signature was the last one outstanding. */
  completed: boolean;
}

export interface SignRequest {
  consent: true;
  signatureDataUrl?: string;
  typedName?: string;
}

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

/** An error carrying the HTTP status the function replied with. */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /** True when the caller needs to sign in again. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

/**
 * Raw transport failures ("Failed to fetch", "NetworkError...") say nothing
 * useful to a person, so they are replaced rather than surfaced.
 */
const NETWORK_NOISE = /failed to fetch|networkerror|load failed|network request failed/i;
const NETWORK_MESSAGE =
  'We could not reach the server. Check your connection and try again.';

/** Turn anything thrown into a message safe to put in front of a person. */
export function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return NETWORK_NOISE.test(err.message) ? NETWORK_MESSAGE : err.message;
  }
  if (err instanceof Error && err.message) {
    return NETWORK_NOISE.test(err.message) ? NETWORK_MESSAGE : err.message;
  }
  return fallback;
}

/* -------------------------------------------------------------------------- */
/* Transport                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * No JWT is minted or attached here. `functions.createExecution` carries the
 * browser's session cookie (the SDK always sends `credentials: 'include''`),
 * and Appwrite recognizes an authenticated caller on its own: it auto-injects
 * `x-appwrite-user-jwt` into the function's request headers server-side. A
 * custom header named anything starting with `x-appwrite-` is reserved and
 * silently dropped, which is exactly why an earlier version of this file that
 * minted its own JWT and sent it as `x-appwrite-jwt` never actually reached
 * the function — see esign-api's `callerFrom`.
 */

function extractServerError(data: unknown): string | null {
  if (typeof data === 'object' && data !== null && 'error' in data) {
    const value = (data as { error: unknown }).error;
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

async function execute<T>(path: string, method: 'GET' | 'POST', payload: unknown): Promise<T> {
  let exec: Models.Execution;
  try {
    exec = await functions.createExecution({
      functionId: ESIGN_FUNCTION_ID,
      xpath: path,
      method: method === 'GET' ? ExecutionMethod.GET : ExecutionMethod.POST,
      body: payload === undefined ? '' : JSON.stringify(payload),
    });
  } catch (err) {
    throw new ApiError(
      messageOf(err, 'We could not reach the signing service. Check your connection and try again.'),
      0,
    );
  }

  const status = exec.responseStatusCode;
  const body = exec.responseBody ?? '';

  if (body === '') {
    throw new ApiError(
      status === 401
        ? 'Your sign-in has expired. Please request a new sign-in link.'
        : 'The signing service did not send a response. Please try again.',
      status === 0 ? 502 : status,
    );
  }

  let data: unknown;
  try {
    data = JSON.parse(body);
  } catch {
    throw new ApiError('The signing service returned an unreadable response.', status);
  }

  if (status < 200 || status >= 300) {
    throw new ApiError(extractServerError(data) ?? `Request failed (${status}).`, status);
  }

  return data as T;
}

const get = <T>(path: string): Promise<T> => execute<T>(path, 'GET', undefined);
const post = <T>(path: string, payload: unknown): Promise<T> => execute<T>(path, 'POST', payload);

/* -------------------------------------------------------------------------- */
/* Routes                                                                     */
/* -------------------------------------------------------------------------- */

/** Everything the signed-in caller may see. */
export const listMyEnvelopes = (): Promise<MyEnvelopes> => get<MyEnvelopes>('/my');

/** One envelope. Fetching it also marks the caller as having viewed it. */
export const getEnvelope = (id: string): Promise<EnvelopeDetail> =>
  get<EnvelopeDetail>(`/envelopes/${encodeURIComponent(id)}`);

/** The PDF bytes - the original, or the executed copy when `signed` is true. */
export const getEnvelopeFile = (id: string, signed = false): Promise<DocumentFile> =>
  get<DocumentFile>(`/envelopes/${encodeURIComponent(id)}/file${signed ? '?signed=1' : ''}`);

/** Record the caller's signature. The server insists on consent. */
export const signEnvelope = (id: string, request: SignRequest): Promise<SignResult> =>
  post<SignResult>(`/envelopes/${encodeURIComponent(id)}/sign`, request);

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Decode a base64 payload into a Blob without blowing the call stack. */
export function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

/** Hand a Blob to the browser as a download, then release the object URL. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Safari needs the URL to outlive the click by a beat.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
